// webSource.ts — SessionSource over a FileSystemDirectoryHandle (browser File
// System Access API), so the QA gate (qaSession.assessSource) can run in the
// web app against a directory the user picked / OPFS. No node imports; bundled
// by esbuild for the browser. lines() streams + splits incrementally — never
// buffers the whole file (tracking.jsonl can be hundreds of MB) — and matches
// node readline semantics: \n and \r\n end lines, terminators stripped, no
// trailing empty line after a final newline.

import type { SessionSource } from "./source.ts";

/** Walk a (possibly nested) relative path to a file handle. */
async function fileHandle(
  root: FileSystemDirectoryHandle,
  rel: string,
): Promise<FileSystemFileHandle> {
  const parts = rel.split("/").filter((p) => p.length > 0);
  let dir = root;
  for (const p of parts.slice(0, -1)) dir = await dir.getDirectoryHandle(p);
  return dir.getFileHandle(parts[parts.length - 1]);
}

async function getFile(root: FileSystemDirectoryHandle, rel: string): Promise<File> {
  return (await fileHandle(root, rel)).getFile();
}

export function webSource(root: FileSystemDirectoryHandle): SessionSource {
  return {
    name: root.name,
    async exists(rel) {
      try {
        await fileHandle(root, rel);
        return true;
      } catch {
        return false;
      }
    },
    async readText(rel) {
      return (await getFile(root, rel)).text();
    },
    async readBytes(rel) {
      return new Uint8Array(await (await getFile(root, rel)).arrayBuffer());
    },
    async *lines(rel) {
      const file = await getFile(root, rel);
      const reader = file
        .stream()
        .pipeThrough(new TextDecoderStream())
        .getReader();
      let buf = "";
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += value;
          let nl: number;
          while ((nl = buf.indexOf("\n")) !== -1) {
            let line = buf.slice(0, nl);
            if (line.endsWith("\r")) line = line.slice(0, -1);
            buf = buf.slice(nl + 1);
            yield line;
          }
        }
      } finally {
        reader.releaseLock();
      }
      // Tail without a trailing newline is still a line; a trailing newline
      // leaves buf empty and yields nothing (readline parity).
      if (buf.length > 0) {
        yield buf.endsWith("\r") ? buf.slice(0, -1) : buf;
      }
    },
  };
}
